/*
  Kalmora — showcase.js
  Hero carrusel: la tarjeta alineada con el inicio de la pista es la activa. Al cambiar:
  - el fondo de la sección pasa al color del producto (--showcase-bg);
  - se muestra su texto (nombre, titular, precio, botón);
  - se actualiza el contador y el estado visual de las tarjetas.
  Flechas y teclado (←/→) cambian el producto al instante y mueven la pista con scroll nativo;
  el swipe del móvil usa el mismo scroll y se sincroniza durante el gesto y al terminar.
*/
(() => {
  function init(section) {
    if (section.dataset.showcaseReady) return;
    section.dataset.showcaseReady = 'true';

    const track = section.querySelector('[data-showcase-track]');
    const slides = Array.from(section.querySelectorAll('[data-showcase-slide]'));
    const texts = Array.from(section.querySelectorAll('[data-showcase-text]'));
    const counter = section.querySelector('[data-showcase-current]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!track || slides.length === 0) return;

    let active = 0;
    // Desplazamiento programado en curso (flechas/teclado): posición destino o null.
    // Mientras existe, el scroll intermedio no reasigna la tarjeta activa.
    let target = null;
    let targetTimer = 0;
    // Posición de encaje de cada tarjeta (la pista es su offsetParent; se descuenta el scroll-padding)
    const snapLeft = (slide) => slide.offsetLeft - (parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0);
    const maxScroll = () => track.scrollWidth - track.clientWidth;

    function setActive(index) {
      if (index === active || index < 0 || index >= slides.length) return;
      active = index;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      texts.forEach((text, i) => {
        const on = i === index;
        text.hidden = !on;
        text.classList.toggle('is-active', on);
      });
      const color = slides[index].dataset.color;
      if (color) section.style.setProperty('--showcase-bg', color);
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
    }

    function releaseTarget() {
      target = null;
      clearTimeout(targetTimer);
    }

    function goTo(index) {
      const i = Math.max(0, Math.min(slides.length - 1, index));
      // Respuesta inmediata: color, texto, precio, CTA y contador cambian al pulsar, sin esperar al scroll
      setActive(i);
      target = Math.min(snapLeft(slides[i]), maxScroll());
      clearTimeout(targetTimer);
      targetTimer = setTimeout(releaseTarget, 1200); // seguridad si el scroll se interrumpe
      track.scrollTo({ left: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    // La activa es la tarjeta alineada más cerca del inicio de la pista (funciona aunque quepan varias)
    function syncFromScroll() {
      const start = track.scrollLeft;
      if (target !== null) {
        if (Math.abs(start - target) > 2) return;
        releaseTarget();
      }
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(snapLeft(slide) - start);
        if (d < distance) {
          distance = d;
          nearest = i;
        }
      });
      // Al final de la pista, la última tarjeta puede no alinearse al inicio: entonces es la activa
      if (start >= maxScroll() - 2) nearest = slides.length - 1;
      setActive(nearest);
    }

    // Durante el gesto: sincronía por fotograma. Al terminar: sincronía final garantizada
    // (scrollend donde existe; si no, un temporizador tras el último evento de scroll).
    let frame = 0;
    let endTimer = 0;
    track.addEventListener(
      'scroll',
      () => {
        if (!frame) {
          frame = requestAnimationFrame(() => {
            frame = 0;
            syncFromScroll();
          });
        }
        clearTimeout(endTimer);
        endTimer = setTimeout(syncFromScroll, 120);
      },
      { passive: true }
    );
    track.addEventListener('scrollend', syncFromScroll);

    // Si la persona toca o arrastra la pista, manda su gesto: se cancela el destino programado
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
      track.addEventListener(type, releaseTarget, { passive: true })
    );

    const prev = section.querySelector('[data-showcase-prev]');
    const next = section.querySelector('[data-showcase-next]');
    if (prev) prev.addEventListener('click', () => goTo(active - 1));
    if (next) next.addEventListener('click', () => goTo(active + 1));

    section.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') goTo(active + 1);
      if (event.key === 'ArrowLeft') goTo(active - 1);
    });

    // Al cambiar el ancho de la ventana, la pista se recoloca en la tarjeta activa
    // (solo el ancho: la barra del navegador móvil cambia el alto al hacer scroll)
    let resizeTimer = 0;
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        releaseTarget();
        track.scrollTo({ left: Math.min(snapLeft(slides[active]), maxScroll()), behavior: 'auto' });
      }, 150);
    });
  }

  const initAll = (scope) => scope.querySelectorAll('[data-showcase]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  initAll(document);
})();
